import {isArray, isFinite, isString} from 'lodash';

import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {isPromise} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {readApiModel} from '../../helpers/apiHelpers';
import {
  ApiElectronMethods,
  ElectronMethodsEnum,
  ElectronMethodsModel,
} from '../../models/frames/electronMethodsModel';
import {AppAction} from '../../store/appActions';
import {Dispatch} from '../../store/appStore';
import {ThunkAction, ThunkAsyncAction} from '../../store/appThunk';
import {
  electronDidEnterFullScreenAction,
  electronDidLeaveFullScreenAction,
  electronZoomFactorUpdatedAction,
} from '../../store/electron/electronActions';
import {Electron} from './electron';
import {
  datadogLogAction,
  downloadDidCompleteElectronAction,
  downloadDidFailElectronAction,
  downloadDidProgressElectronAction,
  grammarlyAuthAction,
  handleSharedContentElectronAction,
  historyBackElectronAction,
  historyForwardElectronAction,
  openEmlFileElectronAsyncAction,
  openHelpElectronAction,
  openSettingsElectronAction,
  openUrlElectronAction,
  postMessageElectronAction,
  printElectronAction,
  showUpdateBannerElectronAction,
  signoutElectronAction,
  trackEventElectronAction,
} from './electronBridgeActions';

/*
 * Types.
 */

type ElectronMethodReturnValue = void | AppAction | ThunkAction<unknown> | ThunkAsyncAction<unknown>;
type ElectronMethod<T extends keyof ElectronMethodsModel, M extends ElectronMethodsModel[T]> = M extends any[]
  ? (...args: M) => ElectronMethodReturnValue
  : () => ElectronMethodReturnValue;
type ElectronMethods = {[T in keyof ElectronMethodsModel]: ElectronMethod<T, ElectronMethodsModel[T]>};

/*
 * Electron method mapping.
 */

const electronMethods: ElectronMethods = {
  // Window.
  [ElectronMethodsEnum.DID_ENTER_FULL_SCREEN]: electronDidEnterFullScreenAction,
  [ElectronMethodsEnum.DID_LEAVE_FULL_SCREEN]: electronDidLeaveFullScreenAction,
  [ElectronMethodsEnum.DID_CHANGE_ZOOM_FACTOR]: electronZoomFactorUpdatedAction,
  // Navigation.
  [ElectronMethodsEnum.HISTORY_BACK]: historyBackElectronAction,
  [ElectronMethodsEnum.HISTORY_FORWARD]: historyForwardElectronAction,
  [ElectronMethodsEnum.OPEN_HELP]: openHelpElectronAction,
  [ElectronMethodsEnum.OPEN_MAILTO_URL]: openUrlElectronAction,
  [ElectronMethodsEnum.OPEN_SETTINGS]: openSettingsElectronAction,
  [ElectronMethodsEnum.OPEN_URL]: openUrlElectronAction,
  // Downloads.
  [ElectronMethodsEnum.DOWNLOAD_DID_COMPLETE]: downloadDidCompleteElectronAction,
  [ElectronMethodsEnum.DOWNLOAD_DID_FAIL]: downloadDidFailElectronAction,
  [ElectronMethodsEnum.DOWNLOAD_DID_PROGRESS]: downloadDidProgressElectronAction,
  // Miscellaneous.
  [ElectronMethodsEnum.POST_MESSAGE]: postMessageElectronAction,
  [ElectronMethodsEnum.PRINT]: printElectronAction,
  [ElectronMethodsEnum.SHOW_UPDATE_BANNER]: showUpdateBannerElectronAction,
  [ElectronMethodsEnum.SIGNOUT]: signoutElectronAction,
  [ElectronMethodsEnum.TRACK_EVENT]: trackEventElectronAction,
  [ElectronMethodsEnum.OPEN_EML_FILE]: openEmlFileElectronAsyncAction,
  [ElectronMethodsEnum.DATADOG_LOG]: datadogLogAction,
  [ElectronMethodsEnum.GRAMMARLY_AUTH]: grammarlyAuthAction,
  [ElectronMethodsEnum.HANDLE_SHARED_CONTENT]: handleSharedContentElectronAction,
};

/*
 * Inbound bridge.
 */

export function setupElectronBridgeInbound(dispatch: Dispatch, electron: Electron) {
  // Make sure we have an IPC to listen on.
  const {ipcRenderer} = electron;
  if (!ipcRenderer) {
    throw new Error('Electron IPC renderer not found.');
  }

  // Handle events coming from the desktop app.
  const onElectronEventAsync = async (methodName: string, methodArguments: ReadonlyArray<unknown>) => {
    if (isOutdatedClientBrokenCall(methodName, methodArguments)) {
      return;
    }

    // Parse our input to ensure correct types.
    const rawElectronMethod = {[methodName]: methodArguments.length > 0 ? methodArguments : undefined};
    const electronMethod = readApiModel(ApiElectronMethods, rawElectronMethod);

    // Find the method.
    if (!isElectronMethodName(methodName, electronMethod)) {
      throw new Error('Electron method not found.');
    }

    const method = electronMethods[methodName];
    if (!method) {
      throw new Error('Electron method not found.');
    }

    // Run the method and get the action, if any.
    const methodAction = method(...((electronMethod[methodName] as any) || []));
    if (!methodAction) {
      return;
    }

    // If one was found, dispatch it.
    if (typeof methodAction !== 'function') {
      dispatch(methodAction);
      return;
    }

    // Look at the result of a thunk action.
    const methodActionResult = dispatch(methodAction);

    // If this is not a promise, stop here.
    if (!isPromise(methodActionResult)) {
      return;
    }

    // Otherwise, wait for the promise to complete.
    await methodActionResult;
  };

  // Listen to events coming from the desktop app.
  ipcRenderer.on('proxy-call', async (_event: unknown, methodName: unknown, methodArguments: unknown) => {
    try {
      // Validate types.
      if (!isString(methodName) || !isArray(methodArguments)) {
        throw new Error('Incorrect Electron proxy call.');
      }

      await onElectronEventAsync(methodName, methodArguments);
    } catch (error) {
      reportToBugsnag(new ExtendedError('Error on Electron proxy call.', error));
    }
  });
}

function isElectronMethodName(
  methodName: string,
  electronMethod: ElectronMethodsModel,
): methodName is keyof ElectronMethodsModel {
  return methodName in electronMethod;
}

// An outdated version of the desktop app is passing downloadDidProgress(url, Infinity).
// This is causing us to report a lot of errors to bugsnag.
// You can remove this code early 2020.
function isOutdatedClientBrokenCall(methodName: string, methodArguments: ReadonlyArray<unknown>) {
  if (methodName !== ElectronMethodsEnum.DOWNLOAD_DID_PROGRESS) {
    return false;
  }

  const [url, progress] = methodArguments;

  return !isString(url) || !isFinite(progress);
}
